import React, { useEffect, useRef, useCallback } from 'react';
import { TAdmarktItem } from 'types/TAdmarkt';
import AdmarktSimilarItem from './AdmarktSimilarItem';
import Classes from './AdmarktSimilarItems.scss';
import { TFeedItem } from 'types/TFeeds';

type TSimilarItem = (TAdmarktItem | TFeedItem) & { itemId: string };

type TAdmarktSimilarItemsListProps = {
  items: TSimilarItem[];
  isExperimentFeedEnabled?: boolean;
  onVisibleItemsChange: (visibleItemIds: Set<string>) => void;
};

const AdmarktSimilarItemsList = ({
  items,
  isExperimentFeedEnabled,
  onVisibleItemsChange,
}: TAdmarktSimilarItemsListProps) => {
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);
  const visibleItemsRef = useRef<Set<string>>(new Set());
  const observerRef = useRef<IntersectionObserver | null>(null);

  // Ensure refs array is the same size as items
  itemRefs.current = items.map((_, i) => itemRefs.current[i] ?? React.createRef<HTMLDivElement>().current);

  const handleIntersection = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      let changed = false;
      entries.forEach((entry) => {
        const itemId = (entry.target as HTMLElement).dataset.itemId;
        if (!itemId) return;

        if (entry.isIntersecting) {
          if (!visibleItemsRef.current.has(itemId)) {
            visibleItemsRef.current.add(itemId);
            changed = true;
          }
        } else {
          if (visibleItemsRef.current.has(itemId)) {
            visibleItemsRef.current.delete(itemId);
            changed = true;
          }
        }
      });

      if (changed) {
        onVisibleItemsChange(new Set(visibleItemsRef.current)); // Report changes
      }
    },
    [onVisibleItemsChange],
  );

  useEffect(() => {
    observerRef.current = new IntersectionObserver(handleIntersection, {
      threshold: 0.5,
    });

    const currentObserver = observerRef.current;
    const currentItemRefs = itemRefs.current;

    currentItemRefs.forEach((ref) => {
      if (ref) {
        currentObserver.observe(ref);
      }
    });

    return () => {
      currentItemRefs.forEach((ref) => {
        if (ref) {
          currentObserver.unobserve(ref);
        }
      });
      visibleItemsRef.current.clear(); // Clear the set on cleanup/re-render
    };
  }, [items, handleIntersection]); // Re-run if items array changes

  return (
    <div className={`${Classes.list} ${isExperimentFeedEnabled ? Classes.experimentEnabled : ''}`}>
      {items.map((item, index) => (
        // Wrap AdmarktSimilarItem in a div with ref and data-item-id
        <div
          key={item.itemId}
          ref={(el) => (itemRefs.current[index] = el)}
          data-item-id={item.itemId} // Store itemId for the observer
        >
          <AdmarktSimilarItem item={item} />
        </div>
      ))}
    </div>
  );
};

export default AdmarktSimilarItemsList;
